import axios from 'axios';
import { getToken as getLegacyToken } from '@/lib/auth';
import { getViewAsCustomer } from '@/lib/view-as';

/**
 * Attaching the right bearer token to myevents' API calls, during and after
 * the move from password sign-in to Clerk.
 *
 * Both are live on purpose. A customer-facing portal cannot be cut over in one
 * step: a Clerk misconfiguration would lock every customer out at once, with
 * no way back in. Preferring Clerk and falling back to the legacy JWT means the
 * old door keeps working until it is deliberately closed.
 *
 * The API accepts both — its auth bridge verifies legacy JWTs and Clerk tokens
 * from either pool — so which one a request carries changes nothing downstream.
 */

/** How the app asks Clerk for a token, registered from inside ClerkProvider. */
interface ClerkTokenSource {
  /** Returns a fresh session token, or null when signed out. */
  readonly getToken: () => Promise<string | null>;
  /** Whether the mounted pool is the BCE-team one. */
  readonly isStaffSession: boolean;
}

let source: ClerkTokenSource | null = null;

/** Clerk's signOut, registered from inside the provider. */
let clerkSignOutFn: (() => Promise<void>) | null = null;

/**
 * Registers Clerk's signOut.
 *
 * @param fn - The sign-out function, or null to clear it.
 */
export function setClerkSignOut(fn: (() => Promise<void>) | null): void {
  clerkSignOutFn = fn;
}

/**
 * Ends the Clerk session, if there is one.
 *
 * Sign-out has to end *both* identities. Clearing only the legacy cookie
 * leaves the Clerk session live, and the sign-in screen sends anyone with a
 * live session straight back into the app — so a half-sign-out reads as a
 * broken sign-out button with no way to switch accounts.
 */
export async function clerkSignOut(): Promise<void> {
  if (!clerkSignOutFn) return;
  try {
    await clerkSignOutFn();
  } catch {
    // The legacy logout still runs; a failed Clerk sign-out must not strand
    // someone in a half-signed-out state.
  }
}

/**
 * Registers the Clerk token getter.
 *
 * Injected from the provider rather than imported, so this module — and the
 * interceptor it installs — has no dependency on Clerk and stays testable.
 *
 * @param next - The token source, or null to clear it.
 */
export function setClerkTokenProvider(next: ClerkTokenSource | null): void {
  source = next;
}

/** Whether the current session is a BCE staff member viewing myevents. */
export function isStaffSession(): boolean {
  return source?.isStaffSession ?? false;
}

/**
 * Installs the request interceptor.
 *
 * Called once at startup, before the app renders. Clerk wins when there is a
 * Clerk session; otherwise the legacy cookie token is used, which is what
 * keeps customers who have not signed in again since the migration working.
 */
export function installAuthInterceptor(): void {
  axios.interceptors.request.use(async (config) => {
    // A Clerk lookup that throws must not take the request down with it —
    // falling through to the legacy token is strictly better than failing.
    let token: string | null = null;
    try {
      token = source ? await source.getToken() : null;
    } catch {
      token = null;
    }

    const bearer = token ?? getLegacyToken();
    if (bearer) {
      config.headers.set?.('Authorization', `Bearer ${bearer}`);
    }

    // Ask the API to serve this request as the customer being viewed. Only
    // sent for a staff session — a customer's own requests must never carry
    // it, and the API refuses it from them regardless.
    if (isStaffSession()) {
      const viewing = getViewAsCustomer();
      if (viewing) config.headers.set?.('X-View-As-Customer', viewing.id);
    }
    return config;
  });
}
